/**
 * 主应用组件
 * 左侧：项目管理面板
 * 右侧：步骤导航 + 页面内容 + 底部导航
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAppState } from './hooks/useAppState';
import { useAuth } from './hooks/useAuth';
import ProjectPanel from './components/ProjectPanel';
import SettingsDialog from './components/SettingsDialog';
import AdminPanel from './components/AdminPanel';
import ToolsPanel from './components/tools/ToolsPanel';
import LoginPage from './pages/LoginPage';
import CasdoorCallback from './pages/CasdoorCallback';
import StepBar from './components/StepBar';
import DocumentAnalysis from './pages/DocumentAnalysis';
import OutlineEdit from './pages/OutlineEdit';
import ContentEdit from './pages/ContentEdit';
import ModuleHome from './modules/ModuleHome';
import PlannedModulePage from './modules/PlannedModulePage';
import ScreeningWorkspace from './modules/ScreeningWorkspace';
import TenderDocWorkspace from './modules/TenderDocWorkspace';
import { useActiveModule } from './modules/useActiveModule';
import { getModuleMeta } from './modules/modules';
import { Cog6ToothIcon, FolderIcon, ShieldCheckIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { ProjectInfo, OutlineSource, GenerationMode } from './types';
import { projectApi, systemConfigApi } from './services/api';
import { createDraftStorage, clearContentByIdForUser } from './utils/draftStorage';

function App() {
  const { user, loading: authLoading, isAdmin, logout, license } = useAuth();
  const { activeModule, lastUsedModuleId, selectModule, backToModuleHome } = useActiveModule(user?.id);
  const {
    state,
    updateConfig,
    setCurrentProject,
    updateStep,
    updateFileContent,
    updateFileName,
    updateAnalysisResults,
    setAnalysisResults,
    updateOutline,
    updateBizOutline,
    updateOutlineSource,
    setGenerationMode,
    nextStep,
    prevStep,
    resetState,
  } = useAppState(user?.id);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [toolsPanelOpen, setToolsPanelOpen] = useState(false);
  const [projectListRefreshTrigger, setProjectListRefreshTrigger] = useState(0);

  // 从数据库加载系统级配置（concurrency, mermaid_default_view 等）
  const [systemConcurrency, setSystemConcurrency] = useState<number>(2);
  const [systemMermaidView, setSystemMermaidView] = useState<'code' | 'chart'>('code');

  const loadSystemConfig = useCallback(async () => {
    try {
      const res = await systemConfigApi.getConfig();
      if (res.data.success && res.data.config) {
        const cfg = res.data.config;
        if (cfg.concurrency !== undefined && cfg.concurrency !== null) {
          setSystemConcurrency(Number(cfg.concurrency));
        }
        if (cfg.mermaid_default_view) {
          setSystemMermaidView(cfg.mermaid_default_view as 'code' | 'chart');
        }
      }
    } catch {
      // 静默失败，使用默认值
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadSystemConfig();
    }
  }, [user, loadSystemConfig]);

  // 当前用户的 localStorage 存储实例
  const draftStore = useMemo(() => createDraftStorage(user?.id ?? 0), [user?.id]);

  // 当前项目是否为只读（共享项目且权限为只读）
  const isReadonly = useMemo(() => {
    return state.currentProject?.source === 'shared' && state.currentProject?.share_permission === 'read';
  }, [state.currentProject?.source, state.currentProject?.share_permission]);

  // Casdoor OAuth 回调路由：无需登录即可访问
  if (window.location.pathname === '/auth/casdoor-callback') {
    return <CasdoorCallback />;
  }

  // 认证加载中
  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-sm text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  // 未登录 -> 显示登录页
  if (!user) {
    return <LoginPage />;
  }

  // 登录后先进入模块首页：招标文件编写 / 招标信息筛选 / 投标文件编写
  if (!activeModule) {
    return (
      <>
        <ModuleHome
          user={user}
          isAdmin={isAdmin}
          allowedFeatures={license?.features ?? null}
          lastUsedModuleId={lastUsedModuleId}
          onSelect={selectModule}
          onOpenAdmin={() => setAdminPanelOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onLogout={() => { void logout(); }}
        />
        {isAdmin && (
          <AdminPanel
            isOpen={adminPanelOpen}
            onClose={() => {
              setAdminPanelOpen(false);
              loadSystemConfig();
            }}
          />
        )}
        <SettingsDialog
          config={state.config}
          onConfigChange={updateConfig}
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          readOnly={isReadonly}
        />
      </>
    );
  }

  // 招标信息筛选模块（已实现）：录入公告 → 资质对比与决策 → 转入投标文件编写
  if (activeModule === 'bid-screening') {
    return (
      <ScreeningWorkspace
        onBack={backToModuleHome}
        onLogout={() => { void logout(); }}
        onOpenBidWriting={() => selectModule('bid-writing')}
      />
    );
  }

  // 招标文件编写模块（已实现）：选模板 + 立项信息 → 目录实例化 → 逐章编制 → 导出
  if (activeModule === 'tender-doc') {
    return (
      <TenderDocWorkspace
        onBack={backToModuleHome}
        onLogout={() => { void logout(); }}
      />
    );
  }

  // 其余规划中模块：入口已就位，先展示模块骨架页
  if (activeModule !== 'bid-writing') {
    return <PlannedModulePage module={getModuleMeta(activeModule)} onBack={backToModuleHome} />;
  }

  const steps = ['标书解析', '目录编辑', '正文编辑'];

  const handleReset = () => {
    if (!window.confirm('确定要清理所有缓存数据并从头开始吗？')) {
      return;
    }
    draftStore.clearAll();
    resetState();
  };

  // 选择项目：从数据库加载完整数据
  const handleProjectSelect = async (project: ProjectInfo) => {
    // 切换项目前清除 contentById 本地缓存，防止旧项目正文泄漏
    if (user?.id) {
      clearContentByIdForUser(user.id);
    }
    setCurrentProject(project);
    try {
      const response = await projectApi.getFullData(project.id);
      const data = response.data;

      // 恢复分析结果
      const analysisResults: Record<string, string> = {};
      for (const r of data.analysis_results) {
        if (r.analysis_type && r.content) {
          analysisResults[r.analysis_type] = r.content;
        }
      }
      const overview = analysisResults['overview'] || '';
      // 技术评分标准和技术要求保持独立，不再合并
      updateAnalysisResults(overview, analysisResults['tech_scoring'] || '', analysisResults['tech_requirements'] || '');
      setAnalysisResults(analysisResults);

      // 恢复文件内容（取最近的上传文件）
      const lastFile = data.upload_files.length > 0
        ? data.upload_files[data.upload_files.length - 1]
        : null;
      updateFileContent(lastFile?.extracted_text || '');
      updateFileName(lastFile?.original_name || '');

      // 恢复目录结构：将平铺节点按 section 转为树形
      const outlineTree = buildOutlineTree(data.outline_nodes, data.chapter_contents, 'tech');
      updateOutline({
        outline: outlineTree,
        project_name: project.name,
        project_overview: overview,
      });

      // 如果是整套投标文件模式，同时构建商务部分目录
      if (project.generation_mode === 'full') {
        const bizNodes = data.outline_nodes.filter((n: any) => n.section === 'biz');
        if (bizNodes.length > 0) {
          const bizOutlineTree = buildOutlineTree(data.outline_nodes, data.chapter_contents, 'biz');
          updateBizOutline({
            outline: bizOutlineTree,
            project_name: project.name,
            project_overview: overview,
          });
        }
      }

      // 恢复目录生成方式
      if (data.outline_source) {
        updateOutlineSource(data.outline_source as OutlineSource);
      }

      // 恢复生成模式（校验合法性）
      if (project.generation_mode) {
        const validModes: GenerationMode[] = ['tech', 'full'];
        setGenerationMode(
          validModes.includes(project.generation_mode as GenerationMode)
            ? (project.generation_mode as GenerationMode)
            : 'tech'
        );
      }

      // 恢复步骤
      updateStep(project.current_step);
    } catch {
      // 加载失败时清空旧数据，避免展示上一个项目的数据
      updateOutline({ outline: [], project_name: project.name, project_overview: '' });
      updateBizOutline(null);
      updateOutlineSource(null);
      updateAnalysisResults('', '', '');
      setAnalysisResults({});
      updateFileContent('');
      updateFileName('');
      updateStep(0);
    }
  };

  // 批量生成完成后刷新项目状态（后端已更新 DB 状态，前端同步）
  const handleProjectRefresh = async () => {
    if (!state.currentProject?.id) return;
    try {
      const res = await projectApi.get(state.currentProject.id);
      const updated = res.data;
      if (updated) {
        // 始终合并最新数据，不做状态比较（避免闭包陈旧问题）
        setCurrentProject({ ...state.currentProject, ...updated });
      }
      // 同时触发侧边栏项目列表刷新（使列表中的状态标签同步更新）
      setProjectListRefreshTrigger(prev => prev + 1);
    } catch {
      // 静默失败，下次选中项目或刷新列表时会自动更新
    }
  };

  // 点击"生成标书"立即将项目状态更新为"编辑中"（纯本地状态更新，即时反馈）
  const handleProjectEditing = () => {
    if (state.currentProject && state.currentProject.status !== 'editing') {
      setCurrentProject({ ...state.currentProject, status: 'editing' });
      // 同时触发侧边栏项目列表刷新
      setProjectListRefreshTrigger(prev => prev + 1);
    }
  };

  // 创建项目：清空上一个项目的旧数据
  const handleProjectCreate = (project: ProjectInfo) => {
    // 清除 contentById localStorage 缓存，防止旧项目正文泄漏到新项目
    if (user?.id) {
      clearContentByIdForUser(user.id);
    }
    updateAnalysisResults('', '', '');
    setAnalysisResults({});
    updateFileContent('');
    updateOutline({
      outline: [],
      project_name: project.name,
      project_overview: '',
    });
    updateBizOutline(null);
    updateOutlineSource(null);
    setGenerationMode('tech');
    setCurrentProject(project);
    updateStep(0);
  };

  // 删除当前项目
  const handleProjectDelete = () => {
    setCurrentProject(null);
    resetState();
  };

  const renderCurrentPage = () => {
    // 没有选中项目时，显示引导页
    if (!state.currentProject) {
      return (
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center py-16">
              <FolderIcon className="mx-auto h-16 w-16 text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">请选择或创建一个项目</h3>
              <p className="mt-2 text-sm text-gray-500">
                在左侧面板中创建新项目，或从项目列表中选择已有项目开始编写标书
              </p>
            </div>
          </div>
        </div>
      );
    }

    switch (state.currentStep) {
      case 0:
        return (
          <DocumentAnalysis
            fileContent={state.fileContent}
            fileName={state.fileName}
            analysisResults={state.analysisResults}
            onFileUpload={(content, name) => {
              updateFileContent(content);
              updateFileName(name);
            }}
            onAnalysisComplete={(results) => {
              setAnalysisResults(results);
              const overview = results['overview'] || '';
              // 技术评分标准和技术要求保持独立
              updateAnalysisResults(overview, results['tech_scoring'] || '', results['tech_requirements'] || '');
            }}
            projectId={state.currentProject.id}
            readOnly={isReadonly}
          />
        );
      case 1:
        return (
          <OutlineEdit
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            outlineData={state.outlineData}
            bizOutlineData={state.bizOutlineData}
            outlineSource={state.outlineSource}
            generationMode={state.generationMode}
            onOutlineGenerated={updateOutline}
            onBizOutlineGenerated={updateBizOutline}
            onGenerationModeChange={async (mode) => {
              setGenerationMode(mode);
              // 保存到后端
              if (state.currentProject) {
                projectApi.update(state.currentProject.id, { generation_mode: mode }).catch(() => {});
              }
            }}
            analysisResults={state.analysisResults}
            projectId={state.currentProject.id}
            projectName={state.currentProject.name}
            outlineDescriptiveLevel={state.currentProject.outline_descriptive_level}
            readOnly={isReadonly}
          />
        );
      case 2:
        return (
          <ContentEdit
            outlineData={state.outlineData}
            bizOutlineData={state.bizOutlineData}
            projectId={state.currentProject.id}
            onOutlineUpdated={updateOutline}
            onBizOutlineUpdated={updateBizOutline}
            onProjectRefresh={handleProjectRefresh}
            onProjectEditing={handleProjectEditing}
            concurrency={systemConcurrency}
            mermaidDefaultView={systemMermaidView}
            projectName={state.currentProject.name}
            companyName={state.currentProject.company_profile_names?.join('、') || ''}
            techRequirements={state.techRequirements}
            generationMode={state.generationMode}
            analysisResults={state.analysisResults}
            outlineSource={state.outlineSource}
            readOnly={isReadonly}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* 左侧项目管理面板 */}
      <ProjectPanel
        currentProject={state.currentProject}
        onProjectSelect={handleProjectSelect}
        onProjectCreate={handleProjectCreate}
        onProjectDelete={handleProjectDelete}
        onToolsClick={() => setToolsPanelOpen(true)}
        refreshTrigger={projectListRefreshTrigger}
      />

      {/* 主内容区域 */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 步骤导航 + 右上角设置 */}
        <div className="sticky top-0 z-50 bg-white shadow-sm px-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <StepBar steps={steps} currentStep={state.currentStep} />
            </div>
            <div className="flex items-center gap-2 ml-4">
              {/* 返回模块首页，切换模块 */}
              <button
                onClick={backToModuleHome}
                className="flex-shrink-0 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                title="返回模块首页，切换模块"
              >
                <Squares2X2Icon className="w-4 h-4 mr-1.5" />
                切换模块
              </button>
              {/* 用户信息 */}
              <span className="text-xs text-gray-500">
                {user.display_name || user.username}
                <span className={`ml-1 inline-flex items-center px-1 py-0.5 rounded text-xs ${
                  isAdmin ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
                }`}>
                  {isAdmin ? '管理员' : user.role === 'enterprise' ? '企业用户' : user.role === 'agent' ? '代理用户' : '普通用户'}
                </span>
              </span>
              {/* 管理面板（仅管理员） */}
              {isAdmin && (
                <button
                  onClick={() => setAdminPanelOpen(true)}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-300 transition-colors"
                  title="管理面板"
                >
                  <ShieldCheckIcon className="w-4 h-4 mr-1.5" />
                  管理面板
                </button>
              )}
              <button
                onClick={() => setSettingsOpen(true)}
                className="flex-shrink-0 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                title="我的设置"
              >
                <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
                我的设置
              </button>
            </div>
          </div>
        </div>

        {/* 页面内容 */}
        <div id="app-main-scroll" className="flex-1 p-6 overflow-y-auto">
          {renderCurrentPage()}
        </div>

        {/* 底部导航按钮 */}
        {state.currentProject && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex justify-between">
              <div className="flex items-center space-x-3">
                {state.currentStep === 0 && (
                  <button
                    onClick={handleReset}
                    title="清理所有缓存数据，从头开始"
                    className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    重置
                  </button>
                )}

                <button
                  onClick={() => updateStep(0)}
                  disabled={state.currentStep === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
                >
                  首页
                </button>

                <button
                  onClick={prevStep}
                  disabled={state.currentStep === 0}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  上一步
                </button>
              </div>

              <button
                onClick={nextStep}
                disabled={state.currentStep === steps.length - 1}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                下一步
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 系统设置弹窗 */}
      <SettingsDialog
        config={state.config}
        onConfigChange={updateConfig}
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        readOnly={isReadonly}
      />

      {/* 管理面板弹窗（仅管理员） */}
      {isAdmin && (
        <AdminPanel
          isOpen={adminPanelOpen}
          onClose={() => {
            setAdminPanelOpen(false);
            // 管理面板关闭后刷新系统配置（确保生成设置立即生效）
            loadSystemConfig();
          }}
        />
      )}

      {/* 工具集面板 */}
      <ToolsPanel
        isOpen={toolsPanelOpen}
        onClose={() => setToolsPanelOpen(false)}
      />
    </div>
  );
}

/**
 * 将平铺的目录节点 + 章节内容 构建为树形结构
 */
function buildOutlineTree(
  nodes: Array<{
    node_id: string;
    parent_node_id: string | null;
    title: string;
    description: string | null;
    word_count_range?: string | null;
    source_requirement_id: string | null;
    source_requirement_title: string | null;
    section?: string;
    stable_id?: string | null;
  }>,
  contents: Array<{ node_id: string; content: string | null; section?: string; stable_id?: string | null }>,
  sectionFilter?: string
) {
  // 按 section 过滤（如指定则只取对应 section 的节点）
  const filteredNodes = sectionFilter
    ? nodes.filter((n) => (n.section || 'tech') === sectionFilter)
    : nodes;

  // 构建 node_id → content 和 stable_id → content 两个映射（按 section 过滤）
  const contentByNodeId: Record<string, string> = {};
  const contentByStableId: Record<string, string> = {};
  contents.forEach((c) => {
    if (c.content) {
      const cSection = c.section || 'tech';
      if (!sectionFilter || cSection === sectionFilter) {
        contentByNodeId[c.node_id] = c.content;
        if (c.stable_id) {
          contentByStableId[c.stable_id] = c.content;
        }
      }
    }
  });

  // 根据 node_id 或 stable_id 匹配内容（stable_id 优先，避免目录重排后内容错位）
  const resolveContent = (nodeId: string, stableId?: string | null): string | undefined => {
    if (stableId && contentByStableId[stableId]) {
      return contentByStableId[stableId];
    }
    return contentByNodeId[nodeId] || undefined;
  };

  // 构建 node_id -> node 映射
  const nodeMap: Record<string, any> = {};
  filteredNodes.forEach((n) => {
    nodeMap[n.node_id] = {
      id: n.node_id,
      stable_id: n.stable_id || undefined,
      title: n.title,
      description: n.description || '',
      word_count_range: n.word_count_range || '800-1500',
      source_requirement_id: n.source_requirement_id || undefined,
      source_requirement_title: n.source_requirement_title || undefined,
      children: undefined as any[] | undefined,
      content: resolveContent(n.node_id, n.stable_id),
    };
  });

  // 构建树形结构
  const roots: any[] = [];
  filteredNodes.forEach((n) => {
    const node = nodeMap[n.node_id];
    if (!n.parent_node_id || !nodeMap[n.parent_node_id]) {
      roots.push(node);
    } else {
      const parent = nodeMap[n.parent_node_id];
      if (!parent.children) parent.children = [];
      parent.children.push(node);
    }
  });

  return roots;
}

export default App;
